import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, Download, Send } from "lucide-react";
import ChapterMarker from "../ChapterMarker";
import { PROFILE, SECTIONS } from "../../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const s = SECTIONS[6];
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Transmission failed");
      toast.success("Transmission received. Signal confirmed.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Transmission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id={s.id}
      style={{ minHeight: `${s.weight * 100}vh` }}
      className="relative flex items-center px-6 md:px-12 lg:px-24 py-24"
      data-testid="section-contact"
    >
      <div className="pointer-events-auto grid w-full max-w-5xl grid-cols-1 md:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.6 }}
        >
          <ChapterMarker number={s.number} label={s.label} />
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-[#E0E6ED]">
            Establish
            <br />
            Connection
          </h2>
          <div className="mt-8 flex flex-col gap-3">
            <a
              href={`mailto:${PROFILE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 font-mono text-sm text-[#8A9AAB] hover:text-[#01FFFF] transition-colors duration-200"
            >
              <Mail size={15} /> {PROFILE.email}
            </a>
            <a
              href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-3 font-mono text-sm text-[#8A9AAB] hover:text-[#01FFFF] transition-colors duration-200"
            >
              <Phone size={15} /> {PROFILE.phone}
            </a>
            <a
              href={PROFILE.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-center gap-3 font-mono text-sm text-[#8A9AAB] hover:text-[#01FFFF] transition-colors duration-200"
            >
              <Linkedin size={15} /> LinkedIn
            </a>
            <a
              href={PROFILE.cvUrl}
              download
              data-testid="download-cv-button"
              className="mt-4 inline-flex w-fit items-center gap-2 border border-[#01FFFF] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#01FFFF] transition-colors duration-200 hover:bg-[#01FFFF] hover:text-black"
            >
              <Download size={14} /> Download CV
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col gap-4 border border-[#FF007F]/20 bg-[#0A0A12]/60 backdrop-blur-xl p-8"
          data-testid="contact-form"
        >
          <input
            required
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="NAME"
            data-testid="contact-form-name-input"
            className="bg-transparent border-b border-[#8A9AAB]/30 py-2 font-mono text-sm text-[#E0E6ED] placeholder:text-[#8A9AAB]/60 focus:border-[#01FFFF] outline-none transition-colors duration-200"
          />
          <input
            required
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="EMAIL"
            data-testid="contact-form-email-input"
            className="bg-transparent border-b border-[#8A9AAB]/30 py-2 font-mono text-sm text-[#E0E6ED] placeholder:text-[#8A9AAB]/60 focus:border-[#01FFFF] outline-none transition-colors duration-200"
          />
          <textarea
            required
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="MESSAGE"
            rows={4}
            data-testid="contact-form-message-input"
            className="bg-transparent border-b border-[#8A9AAB]/30 py-2 font-mono text-sm text-[#E0E6ED] placeholder:text-[#8A9AAB]/60 focus:border-[#01FFFF] outline-none transition-colors duration-200 resize-none"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-form-submit-button"
            className="mt-2 inline-flex items-center justify-center gap-2 border border-[#FF007F] py-3 font-mono text-xs uppercase tracking-widest text-[#FF007F] transition-colors duration-200 hover:bg-[#FF007F] hover:text-black disabled:opacity-50"
          >
            <Send size={13} /> {submitting ? "Transmitting..." : "Send Transmission"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
